// src/pages/DynamicField/DynamicFieldListLayer.jsx

import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import SuccessAlert from "../child/Alert/SuccessAlert";
import DengerAlert from "../child/Alert/DengerAlert";
import {
  handleUnauthorized,
  showConfirmationDialog,
} from "../../utils/apiHelper";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useSelector, useDispatch } from "react-redux";
import {
  deleteDynamicFieldById,
  fetchAllDynamicFields,
  toggleDynamicFieldStatus,
  deleteDynamicFieldAlls,
} from "../../redux/dynamicField/dynamicFieldThunks";
import { setFilters } from "../../redux/dynamicField/dynamicFieldSlice";
import { getModulePermissions } from "../../utils/permissionUtils";
import { formatAction } from "../../utils/formatAction";

const DynamicFieldListLayer = () => {
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { shouldCheckPermissions, canEdit, canView, canDelete, canAdd } =
    getModulePermissions("preferences");
  const headerCheckboxRef = useRef(null);

  const {
    list: items,
    totalPages,
    totalFields,
    loading,
    error,
    filters: {
      currentPage,
      itemsPerPage,
      searchQuery,
      sortColumn,
      sortOrder,
      statusFilter,
      startDate,
      endDate,
    },
  } = useSelector((state) => state.dynamicField);

  // fetch data
  const fetchData = (page) => {
    const params = {
      page,
      limit: itemsPerPage,
      search: searchQuery,
      sortBy: sortColumn,
      sortOrder,
      status: statusFilter,
      startDate: startDate ? moment(startDate).format("YYYY-MM-DD") : "",
      endDate: endDate ? moment(endDate).format("YYYY-MM-DD") : "",
    };
    dispatch(fetchAllDynamicFields({ params, navigate }));
  };

  useEffect(() => {
    fetchData(currentPage);
  }, [
    dispatch,
    navigate,
    currentPage,
    searchQuery,
    itemsPerPage,
    sortOrder,
    sortColumn,
    statusFilter,
    startDate,
    endDate,
  ]);

  // helpers
  // const isSelected = (id) => selectedItems.includes(id);
  const isSelected = (id) => selectedItems.has(id);

  // const handleSelectAll = (e) => {
  //   setSelectedItems(e.target.checked ? items.map((item) => item._id) : []);
  // };

  // const handleSelectItem = (id) => {
  //   setSelectedItems((prev) =>
  //     prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
  //   );
  // };

  // Toggle a single item
  const handleSelectItem = (id) => {
    setSelectedItems((prev) => {
      const updated = new Set(prev);
      if (updated.has(id)) {
        updated.delete(id);
      } else {
        updated.add(id);
      }
      return updated;
    });
  };

  // Select/deselect all on current page
  const handleSelectAll = (checked) => {
    setSelectedItems((prev) => {
      const updated = new Set(prev);
      items.forEach((item) => {
        if (checked) {
          updated.add(item._id);
        } else {
          updated.delete(item._id);
        }
      });
      return updated;
    });
  };

  // Header checkbox helpers
  const currentPageIds = items.map((u) => u._id);
  const isHeaderChecked = () =>
    currentPageIds.length > 0 &&
    currentPageIds.every((id) => selectedItems.has(id));
  const isHeaderIndeterminate = () =>
    currentPageIds.some((id) => selectedItems.has(id)) && !isHeaderChecked();

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = isHeaderIndeterminate();
    }
  }, [items, selectedItems]);

  const handleDelete = async ({ id = null, bulk = false }) => {
    let idsToDelete = bulk ? Array.from(selectedItems) : [id];
    if (!idsToDelete.length) return;

    const confirmMessage = bulk
      ? "This action will delete the selected preferences!"
      : "This action will delete the selected preference!";
    const confirmBtnText = bulk ? "Delete All" : "Delete";
    const cancelBtnText = "Cancel";

    const result = await showConfirmationDialog(
      confirmMessage,
      confirmBtnText,
      cancelBtnText
    );
    if (!result.isConfirmed) return;

    try {
      if (bulk) {
        const response = await dispatch(
          deleteDynamicFieldAlls({ ids: idsToDelete, navigate })
        );
        const res = response.payload;

        if (res.status === 401) return handleUnauthorized(navigate);

        if (res.status === 200 || res.status === true) {
          setSuccessMessage(res.message || "Preferences deleted successfully");
          // setSelectedItems([]);
          setSelectedItems(new Set());
        } else {
          setErrorMessage(res.message || "Failed to delete");
          return;
        }
      } else {
        await dispatch(
          deleteDynamicFieldById({ id: idsToDelete[0], navigate })
        ).unwrap();

        setSuccessMessage("Preference deleted successfully");

        if (items.length === 1 && currentPage > 1) {
          dispatch(setFilters({ currentPage: currentPage - 1 }));
        }
      }

      fetchData(currentPage);
    } catch (err) {
      console.error("Delete error:", err);
      setErrorMessage(err.message || "Failed to delete");
    } finally {
      setTimeout(() => {
        setSuccessMessage("");
        setErrorMessage("");
      }, 3000);
    }
  };

  const updateStatus = async (id, currentStatus) => {
    const newStatus = !currentStatus; // toggle boolean
    const result = await showConfirmationDialog(
      `Do you want to change status to ${newStatus ? "Active" : "Inactive"}?`
    );
    if (!result.isConfirmed) return;

    try {
      await dispatch(
        toggleDynamicFieldStatus({
          id,
          data: { isActive: newStatus },
          navigate,
        })
      ).unwrap();
      setSuccessMessage(
        `Status updated to ${newStatus ? "Active" : "Inactive"}`
      );
    } catch (err) {
      setErrorMessage(err.message || "Failed to update status");
    } finally {
      setTimeout(() => {
        setSuccessMessage("");
        setErrorMessage("");
      }, 3000);
    }
  };

  const handleSort = (column) => {
    const newOrder =
      sortColumn === column && sortOrder === "asc" ? "desc" : "asc";
    dispatch(setFilters({ sortColumn: column, sortOrder: newOrder }));
  };

  const handleItemsPerPageChange = (e) => {
    dispatch(
      setFilters({ itemsPerPage: parseInt(e.target.value, 10), currentPage: 1 })
    );
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      dispatch(setFilters({ currentPage: pageNumber }));
    }
  };

  const getPaginationRange = () => {
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + 4);
    if (end - start < 4) start = Math.max(1, end - 4);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      dispatch(setFilters({ currentPage: currentPage + 1 }));
    }
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      dispatch(setFilters({ currentPage: currentPage - 1 }));
    }
  };

  return (
    <div className="card h-100 p-0 radius-12">
      {successMessage && <SuccessAlert message={successMessage} />}
      {errorMessage && <DengerAlert message={errorMessage} />}

      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        <div className="d-flex align-items-center flex-wrap gap-3">
          <span className="text-md fw-medium text-secondary-light mb-0">
            Show
          </span>
          <select
            className="form-select form-select-sm w-auto ps-12 py-6"
            style={{
              fontSize: "16px",
              borderRadius: "7px",
              backgroundPositionX: "unset",
              backgroundPosition: "right 0.75rem center",
            }}
            value={itemsPerPage}
            defaultValue="Select Number"
            onChange={handleItemsPerPageChange}
          >
            <option value="10">10 items per page</option>
            <option value="20">20 items per page</option>
            <option value="50">50 items per page</option>
            <option value="100">100 items per page</option>
          </select>
          <form className="navbar-search" style={{ position: "relative" }}>
            <input
              type="text"
              className="bg-base h-40-px w-auto pe-40"
              name="search"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => {
                dispatch(
                  setFilters({
                    searchQuery: e.target.value,
                  })
                );
              }}
              onKeyUp={(e) => {
                if (e.target.value === "") {
                  dispatch(setFilters({ currentPage: 1 }));
                }
              }}
              style={{
                paddingRight: searchQuery ? "5rem" : "2.5rem",
              }}
            />

            {/* Search Icon */}
            <Icon
              icon="ion:search-outline"
              className="icon"
              style={{
                position: "absolute",
                right: searchQuery ? "3.25rem" : "1rem",
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: "1.25rem",
                color: "#999",
                pointerEvents: "none",
              }}
            />

            {/* Clear (Close) Icon */}
            {searchQuery && (
              <button
                type="button"
                onClick={() => {
                  dispatch(
                    setFilters({
                      searchQuery: "",
                      currentPage: 1,
                    })
                  );
                }}
                style={{
                  position: "absolute",
                  right: "1rem",
                  top: "30%",
                  transform: "translateY(-50%)",
                  fontSize: "1.25rem",
                  color: "#000",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <Icon icon="ic:round-close" />
              </button>
            )}
          </form>
          <select
            className="form-select form-select-sm w-auto ps-12 py-6"
            style={{
              fontSize: "16px",
              borderRadius: "7px",
              backgroundPositionX: "unset",
              backgroundPosition: "right 0.75rem center",
            }}
            value={statusFilter}
            onChange={(e) =>
              dispatch(
                setFilters({
                  statusFilter: e.target.value,
                  currentPage: 1, // reset to page 1 on filter change
                })
              )
            }
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          {(!shouldCheckPermissions || canDelete) && (
            <button
              className="btn btn-danger"
              onClick={() => handleDelete({ bulk: true })}
              disabled={selectedItems.size === 0}
            >
              Delete All
            </button>
          )}
        </div>
        {(!shouldCheckPermissions || canAdd) && (
          <Link
            to="/preferences/add"
            className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
          >
            <Icon
              icon="ic:baseline-plus"
              className="icon text-xl line-height-1"
            />
            Add Preferences
          </Link>
        )}
      </div>

      {/* table */}
      <div className="card-body p-24">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr>
                <th scope="col">
                  <div className="d-flex align-items-center gap-10">
                    <div className="form-check style-check d-flex align-items-center">
                      <input
                        className="form-check-input radius-4 border input-form-dark"
                        type="checkbox"
                        name="checkbox"
                        // id="selectAll"
                        // onChange={handleSelectAll}
                        // checked={
                        //   selectedItems.length === items.length &&
                        //   items.length > 0
                        // }
                        ref={headerCheckboxRef}
                        checked={isHeaderChecked()}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        style={{
                          backgroundColor: isHeaderIndeterminate()
                            ? "transparent"
                            : undefined,
                          borderColor: isHeaderIndeterminate()
                            ? "#d1d5db80"
                            : undefined,
                        }}
                      />
                    </div>
                  </div>
                </th>
                <th
                  style={{ cursor: "pointer" }}
                  onClick={() => handleSort("title")}
                >
                  <div className="d-flex align-items-center gap-0">
                    Title
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${sortColumn === "title" && sortOrder === "asc"
                          ? "active"
                          : ""
                          }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${sortColumn === "title" && sortOrder === "desc"
                          ? "active"
                          : ""
                          }`}
                      />
                    </span>
                  </div>
                </th>{" "}
                <th
                  style={{ cursor: "pointer" }}
                  onClick={() => handleSort("type")}
                >
                  <div className="d-flex align-items-center gap-0">
                    Type
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${sortColumn === "type" && sortOrder === "asc"
                          ? "active"
                          : ""
                          }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${sortColumn === "type" && sortOrder === "desc"
                          ? "active"
                          : ""
                          }`}
                      />
                    </span>
                  </div>
                </th>{" "}
                <th
                  onClick={() => handleSort("createdAt")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-0">
                    Created At
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${sortColumn === "createdAt" && sortOrder === "asc"
                          ? "active"
                          : ""
                          }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${sortColumn === "createdAt" && sortOrder === "desc"
                          ? "active"
                          : ""
                          }`}
                      />
                    </span>
                  </div>
                </th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="text-center">
                    <span className="loader-15"></span>
                  </td>
                </tr>
              ) : Array.isArray(items) && items.length > 0 ? (
                items.map((field) => (
                  <tr key={field?._id || Math.random()}>
                    <td>
                      <div className="d-flex align-items-center gap-10">
                        <div className="form-check style-check d-flex align-items-center">
                          <input
                            className="form-check-input radius-4 border border-neutral-400"
                            type="checkbox"
                            name="checkbox"
                            value={field?._id}
                            checked={isSelected(field?._id)}
                            onChange={() => handleSelectItem(field?._id)}
                          />
                        </div>
                      </div>
                    </td>
                    <td>
                      <span
                        className="text-md mb-0 fw-normal text-secondary-light d-inline-block text-truncate"
                        style={{ maxWidth: "200px" }}
                        title={field?.title || "N/A"}
                      >
                        {field?.title || "N/A"}
                      </span>
                    </td>{" "}
                    <td>{formatAction(field?.type)}</td>
                    <td>{moment(field?.createdAt).format("DD-MM-YYYY")}</td>
                    <td>
                      {!shouldCheckPermissions || canEdit ? (
                        // ✅ Clickable when user has edit permission
                        <span
                          onClick={() =>
                            updateStatus(field?._id, field?.isActive)
                          }
                          className={`${field?.isActive
                            ? "bg-success-focus text-success-600 border border-success-main"
                            : "bg-neutral-200 text-neutral-600 border border-neutral-400"
                            } px-24 py-4 radius-4 fw-medium text-sm`}
                          style={{ cursor: "pointer" }}
                        >
                          {field?.isActive ? "Active" : "Inactive"}
                        </span>
                      ) : (
                        // 🚫 Non-clickable when user has no edit permission
                        <span
                          className={`${field?.isActive
                            ? "bg-success-focus text-success-600 border border-success-main opacity-50"
                            : "bg-neutral-200 text-neutral-600 border border-neutral-400 opacity-50"
                            } px-24 py-4 radius-4 fw-medium text-sm`}
                          style={{ cursor: "default" }}
                          title="You don’t have permission to change status"
                        >
                          {field?.isActive ? "Active" : "Inactive"}
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-10">
                        {(!shouldCheckPermissions || canView) && (
                          <button
                            title="View"
                            onClick={() =>
                              navigate(`/preferences/view/${field._id}`)
                            }
                            className="bg-info-focus bg-hover-info-200 text-info-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          >
                            <Icon icon="majesticons:eye-line"
                              className="icon text-xl" />
                          </button>
                        )}
                        {(!shouldCheckPermissions || canEdit) && (
                          <button
                            title="Edit"
                            onClick={() =>
                              navigate(`/preferences/edit/${field._id}`)
                            }
                            className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          >
                            <Icon icon="lucide:edit" className="menu-icon" />
                          </button>
                        )}
                        {(!shouldCheckPermissions || canDelete) && (
                          <button
                            title="Delete"
                            onClick={() =>
                              handleDelete({ id: field?._id, bulk: false })
                            }
                            className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          >
                            <Icon icon="fluent:delete-24-regular"
                              className="menu-icon"
                            />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center">
                    No Preferences Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* pagination */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
          <span>
            Showing{" "}
            {items?.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} to{" "}
            {items?.length > 0
              ? Math.min(currentPage * itemsPerPage, totalFields)
              : 0}{" "}
            of {totalFields || 0} entries
          </span>
          <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <Link
                className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                to="#"
                onClick={handlePrev}
              >
                <Icon icon="ep:d-arrow-left" />
              </Link>
            </li>
            {getPaginationRange().map((page) => {
              // Return JSX for pagination items
              return (
                <li key={page} className="page-item">
                  <Link
                    className={`page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${currentPage === page
                      ? "bg-primary-600 text-white"
                      : "bg-neutral-200"
                      }`}
                    to="#"
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </Link>
                </li>
              );
            })}

            <li
              className={`page-item ${currentPage === totalPages ? "disabled" : ""
                }`}
            >
              <Link
                className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                to="#"
                onClick={handleNext}
              >
                <Icon icon="ep:d-arrow-right" />
              </Link>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default DynamicFieldListLayer;
